
  // src/pages/SubscriptionPage.jsx

  import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
  import { useNavigate } from 'react-router-dom';
  import { useAuth } from '../context/AuthContext';
  import API from '../api';
  import { toast } from 'react-toastify';
  import { isElectron } from '../utils/platform';
  import DOMPurify from 'dompurify';

  // ============ SECURITY UTILITIES ============
  const sanitize = (str) => {
    if (typeof str !== 'string') return str;
    return DOMPurify.sanitize(str, { ALLOWED_TAGS: [] });
  };

  const isValidPaymentId = (id) => {
    if (!id || typeof id !== 'string') return false;
    return /^[a-f\d]{24}$/i.test(id) || /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i.test(id);
  };

  const secureStorage = {
    set: (key, value, expiryMinutes = 30) => {
      const item = {
        value,
        expiry: Date.now() + expiryMinutes * 60 * 1000,
      };
      try {
        sessionStorage.setItem(key, JSON.stringify(item));
      } catch (e) {
        console.error('Storage error:', e);
      }
    },
    get: (key) => {
      try {
        const itemStr = sessionStorage.getItem(key);
        if (!itemStr) return null;
        const item = JSON.parse(itemStr);
        if (Date.now() > item.expiry) {
          sessionStorage.removeItem(key);
          return null;
        }
        return item.value;
      } catch (e) {
        return null;
      }
    },
    remove: (key) => sessionStorage.removeItem(key)
  };

  // ============ ICONS ============
  const Icons = {
    Check: () => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    Shield: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    Clock: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    AlertTriangle: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
    CheckCircle: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    Gift: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 12 20 22 4 22 4 12" />
        <rect x="2" y="7" width="20" height="5" />
        <line x1="12" y1="22" x2="12" y2="7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
      </svg>
    ),
    CreditCard: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
    Zap: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
    Crown: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06l-4.23 1.08-3.53-5.19c-.52-.77-1.53-.97-2.27-.45-.29.2-.5.49-.62.8L6.55 9.66l-4.23-1.08c-.8-.21-1.63.26-1.84 1.06-.11.41-.02.85.24 1.18l4.63 5.82c.47.59 1.25.81 1.95.56l4.7-1.62 4.7 1.62c.7.25 1.48.03 1.95-.56l4.63-5.82c.26-.33.35-.77.24-1.18z" />
      </svg>
    ),
    ArrowRight: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    ),
    Loader: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
        <line x1="2" y1="12" x2="6" y2="12" />
        <line x1="18" y1="12" x2="22" y2="12" />
        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
        <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
      </svg>
    ),
    History: () => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v5h5" />
        <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
        <path d="M12 7v5l4 2" />
      </svg>
    ),
    Info: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
    X: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
  };

  // ============ COMPONENT ============
  export default function SubscriptionPage() {
    const navigate = useNavigate();
    const formRef = useRef(null);
    const pollingRef = useRef(null);
    const { user, subscription, refreshUser } = useAuth();

    const [plans, setPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingPlan, setProcessingPlan] = useState(null);
    const [userStatus, setUserStatus] = useState(null);
    const [trialInfo, setTrialInfo] = useState(null);
    const [esewaData, setEsewaData] = useState(null);
    const [pollingStatus, setPollingStatus] = useState(null);
    const [selectedBilling, setSelectedBilling] = useState('all');

    useEffect(() => {
      fetchData();
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (user) {
        setUserStatus(user.status);
        setTrialInfo(user.trial);
      }
      if (subscription) {
        setCurrentSubscription(subscription);
      }
    }, [user, subscription]);

    useEffect(() => {
      if (esewaData && formRef.current && !isElectron()) {
        setTimeout(() => {
          formRef.current.submit();
        }, 100);
      }
    }, [esewaData]);

    const fetchData = useCallback(async () => {
      setLoading(true);
      try {
        const [plansRes, subRes] = await Promise.all([
          API.get('/subscriptions/plans'),
          API.get('/subscriptions/my-subscription')
        ]);

        const plansData = Array.isArray(plansRes.data?.plans) ? plansRes.data.plans : [];
        setPlans(plansData);
        setCurrentSubscription(subRes.data?.currentSubscription || null);
        setSubscriptionHistory(Array.isArray(subRes.data?.history) ? subRes.data.history : []);
        setUserStatus(subRes.data?.userStatus || null);
        setTrialInfo(subRes.data?.trial || null);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load subscription data');
      }
      setLoading(false);
    }, []);

    const startPaymentPolling = useCallback((paymentId) => {
      if (!isValidPaymentId(paymentId)) {
        toast.error('Invalid payment reference');
        setProcessingPlan(null);
        return;
      }

      let attempts = 0;
      const maxAttempts = 60;

      setPollingStatus('Waiting for payment completion...');

      pollingRef.current = setInterval(async () => {
        attempts++;

        try {
          const response = await API.get(`/payments/${encodeURIComponent(paymentId)}`);
          const payment = response.data?.payment;

          if (payment?.status === 'completed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setPollingStatus(null);
            setProcessingPlan(null);

            await refreshUser();
            secureStorage.remove('pendingPayment');

            toast.success('🎉 Payment successful! Subscription activated!', {
              autoClose: 5000
            });

            navigate(`/payment-success?oid=${encodeURIComponent(paymentId)}&polled=true`);

          } else if (payment?.status === 'failed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setPollingStatus(null);
            setProcessingPlan(null);
            secureStorage.remove('pendingPayment');

            toast.error('Payment failed. Please try again.');
            navigate(`/payment-failed?oid=${encodeURIComponent(paymentId)}`);
          }

        } catch (error) {
          
        }

        if (attempts % 6 === 0) {
          setPollingStatus(`Still waiting... (${Math.floor(attempts * 5 / 60)} min)`);
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPollingStatus(null);
          setProcessingPlan(null);

          toast.warning('Payment verification timed out. Please check your subscription status.', {
            autoClose: 10000
          });
        }

      }, 5000);
    }, [navigate, refreshUser]);

    const cancelPolling = useCallback(() => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setPollingStatus(null);
      setProcessingPlan(null);
      secureStorage.remove('pendingPayment');
      toast.info('Payment cancelled');
    }, []);

    const handleSubscribe = useCallback(async (planId) => {
      if (!isValidPaymentId(planId)) {
        toast.error('Invalid plan selected');
        return;
      }

      const lastAttempt = secureStorage.get('lastPaymentAttempt');
      if (lastAttempt && Date.now() - lastAttempt < 3000) {
        toast.warning('Please wait before trying again');
        return;
      }
      secureStorage.set('lastPaymentAttempt', Date.now(), 1);

      setProcessingPlan(planId);

      try {
        const response = await API.post('/payments/initiate', { planId });

        if (response.data?.success) {
          const paymentId = response.data.payment?.id;

          if (!isValidPaymentId(paymentId)) {
            throw new Error('Invalid payment response');
          }

          secureStorage.set('pendingPayment', {
            paymentId: paymentId,
            transactionUuid: response.data.payment?.transactionUuid,
            timestamp: Date.now()
          }, 30);

          if (isElectron()) {
            const esewaUrl = response.data.esewa?.paymentUrl;
            const formData = response.data.esewa?.formData;

            if (!esewaUrl || !formData) {
              throw new Error('Invalid eSewa data');
            }

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = esewaUrl;
            form.target = '_blank';
            form.style.display = 'none';

            Object.entries(formData).forEach(([key, value]) => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = sanitize(key);
              input.value = sanitize(String(value));
              form.appendChild(input);
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);

            toast.info('Complete payment in your browser.', { autoClose: 10000 });
            startPaymentPolling(paymentId);

          } else {
            toast.info('Redirecting to eSewa...', { autoClose: 2000 });
            setEsewaData({
              url: response.data.esewa?.paymentUrl,
              formData: response.data.esewa?.formData
            });
          }
        }
      } catch (error) {
        console.error('Payment error:', error);
        toast.error(error.response?.data?.message || 'Failed to initiate payment');
        setProcessingPlan(null);
      }
    }, [startPaymentPolling]);

    const formatPrice = useCallback((priceInPaisa) => {
      const amount = Number(priceInPaisa) || 0;
      return `Rs. ${(amount / 100).toLocaleString('en-NP')}`;
    }, []);

    const formatDate = useCallback((date) => {
      try {
        return new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return 'Invalid date';
      }
    }, []);

    const getDaysRemaining = useCallback(() => {
      if (currentSubscription?.endDate) {
        const end = new Date(currentSubscription.endDate);
        const now = new Date();
        return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
      }
      return currentSubscription?.daysRemaining || 0;
    }, [currentSubscription]);

    // ============ LOADING STATE ============
    if (loading) {
      return (
        <div className="subscription-page">
          <div className="loading-container">
            <div className="loading-spinner" />
            <h3>Loading Plans</h3>
            <p>Please wait while we fetch the best plans for you...</p>
          </div>
          <style>{styles}</style>
        </div>
      );
    }

    return (
      <div className="subscription-page">
        {/* Hidden eSewa Form */}
        {esewaData && !isElectron() && (
          <form ref={formRef} action={esewaData.url} method="POST" style={{ display: 'none' }}>
            {Object.entries(esewaData.formData || {}).map(([key, value]) => (
              <input key={key} type="hidden" name={sanitize(key)} value={sanitize(String(value))} />
            ))}
          </form>
        )}

        {/* Polling Overlay */}
        {pollingStatus && (
          <div className="overlay">
            <div className="overlay-card">
              <div className="overlay-close" onClick={cancelPolling}>
                <Icons.X />
              </div>
              <div className="overlay-icon">
                <div className="pulse-ring" />
                <Icons.CreditCard />
              </div>
              <h2>Processing Payment</h2>
              <p>Complete the payment in your browser. This window will update automatically once the payment is confirmed.</p>
              <div className="overlay-status">
                <div className="status-dot" />
                {pollingStatus}
              </div>
              <button className="btn-cancel" onClick={cancelPolling}>
                Cancel Payment
              </button>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-badge">
            <Icons.Zap />
            <span>Simple, Transparent Pricing</span>
          </div>
          <h1>Choose Your Perfect Plan</h1>
          <p>Unlock the full potential of PharmaLedger. No hidden fees, cancel anytime.</p>
          
          <div className="payment-trust">
            <div className="trust-item">
              <Icons.Shield />
              <span>Secure Payment</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <img src="https://esewa.com.np/common/images/esewa-icon-large.png" alt="eSewa" className="esewa-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <span>Pay with eSewa</span>
            </div>
            {isElectron() && (
              <>
                <div className="trust-divider" />
                <div className="trust-item desktop-badge">
                  <span>🖥️ Desktop App</span>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Status Alert */}
        {userStatus === 'active' && currentSubscription ? (
          <div className="status-alert status-active">
            <div className="status-icon">
              <Icons.CheckCircle />
            </div>
            <div className="status-content">
              <div className="status-header">
                <h3>Active Subscription</h3>
                <span className="status-badge active">{getDaysRemaining()} days left</span>
              </div>
              <p>
                You're on <strong>{sanitize(currentSubscription.planSnapshot?.name || currentSubscription.planName)}</strong> plan, 
                valid until <strong>{formatDate(currentSubscription.endDate)}</strong>
              </p>
            </div>
          </div>
        ) : userStatus === 'trial' && trialInfo && !trialInfo.isExpired ? (
          <div className="status-alert status-trial">
            <div className="status-icon">
              <Icons.Gift />
            </div>
            <div className="status-content">
              <div className="status-header">
                <h3>Free Trial</h3>
                <span className="status-badge trial">{trialInfo.daysRemaining} days left</span>
              </div>
              <p>Upgrade now to continue enjoying all premium features without interruption.</p>
            </div>
          </div>
        ) : (
          <div className="status-alert status-expired">
            <div className="status-icon">
              <Icons.AlertTriangle />
            </div>
            <div className="status-content">
              <div className="status-header">
                <h3>{userStatus === 'blocked' ? 'Account Restricted' : 'Subscription Required'}</h3>
              </div>
              <p>Choose a plan below to unlock all features and start managing your pharmacy efficiently.</p>
            </div>
          </div>
        )}

        {/* Plans Section */}
        <section className="plans-section">
          <div className="plans-grid">
            {plans.map((plan, index) => {
              const isPopular = plan.isPopular;
              const isProcessing = processingPlan === plan._id;
              const savings = plan.displayPrice && plan.displayPrice > plan.price 
                ? Math.round(((plan.displayPrice - plan.price) / plan.displayPrice) * 100)
                : plan.discount || 0;

              return (
                <div 
                  key={plan._id} 
                  className={`plan-card ${isPopular ? 'popular' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {isPopular && (
                    <div className="popular-badge">
                      <Icons.Crown />
                      <span>Most Popular</span>
                    </div>
                  )}

                  <div className="plan-header">
                    <h2>{sanitize(plan.name)}</h2>
                    <p className="plan-duration">{plan.duration} Month{plan.duration > 1 ? 's' : ''}</p>
                  </div>

                  <div className="plan-pricing">
                    {plan.displayPrice && plan.displayPrice > plan.price && (
                      <span className="original-price">{formatPrice(plan.displayPrice)}</span>
                    )}
                    <div className="current-price">
                      <span className="currency">Rs.</span>
                      <span className="amount">{((plan.finalPrice || plan.price) / 100).toLocaleString('en-NP')}</span>
                    </div>
                    {savings > 0 && (
                      <span className="savings-badge">Save {savings}%</span>
                    )}
                    <p className="monthly-breakdown">
                      {formatPrice(Math.round((plan.finalPrice || plan.price) / plan.duration))}/month
                    </p>
                  </div>

                  {plan.features && plan.features.length > 0 && (
                    <ul className="features-list">
                      {plan.features.map((feature, idx) => (
                        <li key={idx}>
                          <span className="feature-check"><Icons.Check /></span>
                          <span>{sanitize(feature)}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    className={`btn-subscribe ${isPopular ? 'primary' : 'secondary'} ${isProcessing ? 'loading' : ''}`}
                    onClick={() => handleSubscribe(plan._id)}
                    disabled={isProcessing || pollingStatus}
                  >
                    {isProcessing ? (
                      <>
                        <span className="btn-spinner" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>{userStatus === 'active' ? 'Extend Plan' : 'Get Started'}</span>
                        <Icons.ArrowRight />
                      </>
                    )}
                  </button>

                  {isPopular && (
                    <p className="plan-note">🔥 Chosen by 80% of pharmacies</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Test Credentials */}
        <section className="test-section">
          <div className="test-card">
            <div className="test-header">
              <Icons.Info />
              <h3>eSewa Test Credentials</h3>
            </div>
            <div className="test-grid">
              <div className="test-item">
                <label>eSewa ID</label>
                <code>9806800001</code>
              </div>
              <div className="test-item">
                <label>Password</label>
                <code>Nepal@123</code>
              </div>
              <div className="test-item">
                <label>MPIN/OTP</label>
                <code>123456</code>
              </div>
            </div>
            {isElectron() && (
              <p className="test-note">
                💡 Payment page opens in your default browser. Complete the payment there.
              </p>
            )}
          </div>
        </section>

        {/* History Section */}
        {subscriptionHistory.length > 0 && (
          <section className="history-section">
            <div className="section-header">
              <Icons.History />
              <h2>Payment History</h2>
            </div>
            <div className="history-table-wrapper">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionHistory.map((sub) => (
                    <tr key={sub._id}>
                      <td>
                        <div className="plan-cell">
                          <strong>{sanitize(sub.planSnapshot?.name || 'N/A')}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="period-cell">
                          <span>{formatDate(sub.startDate)}</span>
                          <span className="period-arrow">→</span>
                          <span>{formatDate(sub.endDate)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`table-badge ${sub.status}`}>
                          {sanitize(sub.status)}
                        </span>
                      </td>
                      <td>
                        <strong>{formatPrice(sub.planSnapshot?.price || 0)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>Can I upgrade my plan later?</h4>
              <p>Yes, you can upgrade anytime. The remaining days will be added to your new subscription.</p>
            </div>
            <div className="faq-item">
              <h4>What payment methods do you accept?</h4>
              <p>We currently accept eSewa for secure and convenient payments.</p>
            </div>
            <div className="faq-item">
              <h4>Is there a refund policy?</h4>
              <p>We offer a 7-day money-back guarantee if you're not satisfied.</p>
            </div>
            <div className="faq-item">
              <h4>What happens when my subscription expires?</h4>
              <p>You'll have limited access. Renew anytime to restore full functionality.</p>
            </div>
          </div>
        </section>

        <style>{styles}</style>
      </div>
    );
  }

  // ============ STYLES ============
  const styles = `
    /* Base Reset */
    .subscription-page {
      min-height: 100vh;
      background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      padding-bottom: 80px;
    }

    /* Loading State */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 80vh;
      text-align: center;
    }

    .loading-spinner {
      width: 48px;
      height: 48px;
      border: 3px solid #e2e8f0;
      border-top-color: #3b82f6;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin-bottom: 24px;
    }

    .loading-container h3 {
      font-size: 1.25rem;
      color: #1e293b;
      margin: 0 0 8px;
    }

    .loading-container p {
      color: #64748b;
      margin: 0;
    }

    /* Hero Section */
    .hero-section {
      text-align: center;
      padding: 60px 24px 40px;
      max-width: 800px;
      margin: 0 auto;
    }

    .hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
      color: #92400e;
      padding: 8px 20px;
      border-radius: 100px;
      font-size: 0.875rem;
      font-weight: 600;
      margin-bottom: 24px;
    }

    .hero-section h1 {
      font-size: 2.75rem;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 16px;
      letter-spacing: -0.02em;
      line-height: 1.2;
    }

    .hero-section > p {
      font-size: 1.125rem;
      color: #64748b;
      margin: 0 0 32px;
      max-width: 500px;
      margin-left: auto;
      margin-right: auto;
    }

    .payment-trust {
      display: inline-flex;
      align-items: center;
      gap: 16px;
      background: white;
      padding: 12px 24px;
      border-radius: 16px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.05);
    }

    .trust-item {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #475569;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .trust-item svg {
      color: #22c55e;
    }

    .esewa-logo {
      height: 20px;
      width: auto;
    }

    .trust-divider {
      width: 1px;
      height: 24px;
      background: #e2e8f0;
    }

    .desktop-badge {
      background: #eff6ff;
      padding: 4px 12px;
      border-radius: 20px;
      color: #3b82f6;
    }

    /* Status Alerts */
    .status-alert {
      display: flex;
      gap: 16px;
      max-width: 900px;
      margin: 0 auto 40px;
      padding: 20px 24px;
      border-radius: 16px;
      align-items: flex-start;
    }

    .status-active {
      background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
      border: 1px solid #a7f3d0;
    }

    .status-active .status-icon { color: #10b981; }

    .status-trial {
      background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
      border: 1px solid #fcd34d;
    }

    .status-trial .status-icon { color: #f59e0b; }

    .status-expired {
      background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
      border: 1px solid #fca5a5;
    }

    .status-expired .status-icon { color: #ef4444; }

    .status-icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .status-content {
      flex: 1;
    }

    .status-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 4px;
    }

    .status-header h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 700;
      color: #1e293b;
    }

    .status-badge {
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 700;
    }

    .status-badge.active {
      background: #10b981;
      color: white;
    }

    .status-badge.trial {
      background: #f59e0b;
      color: white;
    }

    .status-content p {
      margin: 0;
      font-size: 0.9rem;
      color: #475569;
    }

    /* Plans Section */
    .plans-section {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 24px;
    }

    .plans-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 24px;
      max-width: 1100px;
      margin: 0 auto;
    }

    /* Plan Card */
    .plan-card {
      background: white;
      border-radius: 24px;
      padding: 32px;
      border: 1px solid #e2e8f0;
      position: relative;
      display: flex;
      flex-direction: column;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      animation: fadeInUp 0.5s ease forwards;
      opacity: 0;
    }

    .plan-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 40px rgba(0,0,0,0.08);
    }

    .plan-card.popular {
      border: 2px solid #3b82f6;
      box-shadow: 0 8px 30px rgba(59, 130, 246, 0.15);
      transform: scale(1.02);
    }

    .plan-card.popular:hover {
      transform: scale(1.02) translateY(-4px);
      box-shadow: 0 20px 50px rgba(59, 130, 246, 0.2);
    }

    .popular-badge {
      position: absolute;
      top: -14px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 6px;
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: white;
      padding: 8px 20px;
      border-radius: 100px;
      font-size: 0.8rem;
      font-weight: 700;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    }

    .plan-header {
      text-align: center;
      margin-bottom: 24px;
    }

    .plan-header h2 {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 4px;
    }

    .plan-duration {
      color: #94a3b8;
      font-size: 0.9rem;
      margin: 0;
    }

    .plan-pricing {
      text-align: center;
      padding: 24px 0;
      margin-bottom: 24px;
      border-top: 1px solid #f1f5f9;
      border-bottom: 1px solid #f1f5f9;
    }

    .original-price {
      color: #94a3b8;
      text-decoration: line-through;
      font-size: 1rem;
      display: block;
      margin-bottom: 4px;
    }

    .current-price {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      gap: 4px;
      margin-bottom: 8px;
    }

    .current-price .currency {
      font-size: 1.25rem;
      font-weight: 600;
      color: #64748b;
      margin-top: 8px;
    }

    .current-price .amount {
      font-size: 3.5rem;
      font-weight: 800;
      color: #0f172a;
      line-height: 1;
      letter-spacing: -0.02em;
    }

    .savings-badge {
      display: inline-block;
      background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
      color: #16a34a;
      padding: 6px 14px;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 700;
      margin-bottom: 8px;
    }

    .monthly-breakdown {
      color: #64748b;
      font-size: 0.9rem;
      margin: 0;
    }

    /* Features */
    .features-list {
      list-style: none;
      padding: 0;
      margin: 0 0 24px;
      flex: 1;
    }

    .features-list li {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 10px 0;
      color: #475569;
      font-size: 0.9rem;
    }

    .feature-check {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #ecfdf5;
      color: #10b981;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: 1px;
    }

    /* Subscribe Button */
    .btn-subscribe {
      width: 100%;
      padding: 16px 24px;
      border-radius: 12px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: all 0.2s ease;
      border: none;
      position: relative;
      overflow: hidden;
    }

    .btn-subscribe.secondary {
      background: #f1f5f9;
      color: #475569;
    }

    .btn-subscribe.secondary:hover:not(:disabled) {
      background: #e2e8f0;
      color: #1e293b;
    }

    .btn-subscribe.primary {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: white;
      box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4);
    }

    .btn-subscribe.primary:hover:not(:disabled) {
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
      transform: translateY(-1px);
    }

    .btn-subscribe:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }

    .btn-subscribe.loading {
      background: #94a3b8;
      color: white;
    }

    .btn-spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    .plan-note {
      text-align: center;
      font-size: 0.8rem;
      color: #f59e0b;
      margin: 16px 0 0;
      font-weight: 600;
    }

    /* Test Section */
    .test-section {
      max-width: 700px;
      margin: 60px auto 0;
      padding: 0 24px;
    }

    .test-card {
      background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
      border: 1px solid #fde68a;
      border-radius: 16px;
      padding: 24px;
    }

    .test-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 16px;
      color: #92400e;
    }

    .test-header h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
    }

    .test-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }

    .test-item {
      background: rgba(255,255,255,0.8);
      padding: 12px 16px;
      border-radius: 10px;
      text-align: center;
    }

    .test-item label {
      display: block;
      font-size: 0.75rem;
      color: #92400e;
      margin-bottom: 4px;
      font-weight: 500;
    }

    .test-item code {
      font-size: 1rem;
      font-weight: 700;
      color: #1e293b;
      font-family: 'SF Mono', Monaco, Consolas, monospace;
    }

    .test-note {
      margin: 16px 0 0;
      font-size: 0.85rem;
      color: #b45309;
    }

    /* History Section */
    .history-section {
      max-width: 1000px;
      margin: 60px auto 0;
      padding: 0 24px;
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;
      color: #1e293b;
    }

    .section-header h2 {
      margin: 0;
      font-size: 1.375rem;
      font-weight: 700;
    }

    .history-table-wrapper {
      background: white;
      border-radius: 16px;
      border: 1px solid #e2e8f0;
      overflow: hidden;
    }

    .history-table {
      width: 100%;
      border-collapse: collapse;
    }

    .history-table th {
      text-align: left;
      padding: 14px 20px;
      background: #f8fafc;
      color: #64748b;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-bottom: 1px solid #e2e8f0;
    }

    .history-table td {
      padding: 16px 20px;
      border-bottom: 1px solid #f1f5f9;
      font-size: 0.9rem;
      color: #475569;
    }

    .history-table tr:last-child td {
      border-bottom: none;
    }

    .history-table tr:hover {
      background: #fafafa;
    }

    .period-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .period-arrow {
      color: #94a3b8;
    }

    .table-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: capitalize;
    }

    .table-badge.active {
      background: #dcfce7;
      color: #16a34a;
    }

    .table-badge.extended {
      background: #dbeafe;
      color: #2563eb;
    }

    .table-badge.expired {
      background: #fee2e2;
      color: #dc2626;
    }

    /* FAQ Section */
    .faq-section {
      max-width: 900px;
      margin: 80px auto 0;
      padding: 0 24px;
    }

    .faq-section h2 {
      text-align: center;
      font-size: 1.75rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 40px;
    }

    .faq-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 24px;
    }

    .faq-item {
      background: white;
      padding: 24px;
      border-radius: 16px;
      border: 1px solid #e2e8f0;
    }

    .faq-item h4 {
      font-size: 1rem;
      font-weight: 600;
      color: #1e293b;
      margin: 0 0 8px;
    }

    .faq-item p {
      font-size: 0.9rem;
      color: #64748b;
      margin: 0;
      line-height: 1.6;
    }

    /* Overlay */
    .overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.85);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 24px;
    }

    .overlay-card {
      background: white;
      border-radius: 24px;
      padding: 48px;
      max-width: 420px;
      width: 100%;
      text-align: center;
      position: relative;
      animation: fadeInUp 0.3s ease;
    }

    .overlay-close {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #f1f5f9;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #64748b;
      transition: all 0.2s;
    }

    .overlay-close:hover {
      background: #e2e8f0;
      color: #1e293b;
    }

    .overlay-icon {
      position: relative;
      width: 80px;
      height: 80px;
      margin: 0 auto 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
      border-radius: 50%;
      color: #3b82f6;
    }

    .pulse-ring {
      position: absolute;
      inset: -8px;
      border: 2px solid #3b82f6;
      border-radius: 50%;
      animation: pulse 2s ease-in-out infinite;
    }

    .overlay-card h2 {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 12px;
    }

    .overlay-card > p {
      color: #64748b;
      font-size: 0.95rem;
      line-height: 1.6;
      margin: 0 0 24px;
    }

    .overlay-status {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      background: #f0fdf4;
      color: #16a34a;
      padding: 14px 20px;
      border-radius: 12px;
      font-size: 0.9rem;
      font-weight: 600;
      margin-bottom: 24px;
    }

    .status-dot {
      width: 8px;
      height: 8px;
      background: #22c55e;
      border-radius: 50%;
      animation: blink 1s ease-in-out infinite;
    }

    .btn-cancel {
      background: #f1f5f9;
      color: #64748b;
      border: none;
      padding: 14px 32px;
      border-radius: 10px;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .btn-cancel:hover {
      background: #e2e8f0;
      color: #475569;
    }

    /* Animations */
    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.15);
        opacity: 0;
      }
    }

    @keyframes blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.4; }
    }

    /* Responsive */
    @media (max-width: 768px) {
      .hero-section h1 {
        font-size: 2rem;
      }

      .plans-grid {
        grid-template-columns: 1fr;
        max-width: 400px;
      }

      .plan-card.popular {
        transform: none;
      }

      .plan-card.popular:hover {
        transform: translateY(-4px);
      }

      .test-grid {
        grid-template-columns: 1fr;
      }

      .payment-trust {
        flex-wrap: wrap;
        justify-content: center;
      }

      .trust-divider {
        display: none;
      }

      .history-table th,
      .history-table td {
        padding: 12px 16px;
      }

      .period-cell {
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
      }

      .period-arrow {
        display: none;
      }
    }

    @media (max-width: 480px) {
      .hero-section {
        padding: 40px 16px 24px;
      }

      .status-alert {
        flex-direction: column;
        text-align: center;
      }

      .status-header {
        justify-content: center;
        flex-wrap: wrap;
      }

      .plan-card {
        padding: 24px;
      }

      .current-price .amount {
        font-size: 2.75rem;
      }

      .overlay-card {
        padding: 32px 24px;
      }
    }
  `;
